import { api } from '@smena/ui';

export interface PropertyRow {
  id: string;
  title: string;
  city: string | null;
  data: Record<string, unknown> & {
    address?: string;
    checkinTime?: string;
    checkoutTime?: string;
    wifi?: { ssid?: string; pass?: string };
    lock?: { code?: string; type?: string; help?: string };
    intercom?: string;
    emergency?: { ukPhone?: string; masterName?: string; masterPhone?: string };
    parking?: string | { free?: string; paid?: string };
    directions?: string;
    rules?: { maxGuests?: string; smoking?: string; pets?: string; kids?: string };
  };
  createdAt: string;
  updatedAt: string;
  bookingsCount: number;
  /** id объекта в кабинете RealtyCalendar, если карточка с ним связана. */
  rcObjectId: string | null;
  /** Карточку завёл инжест; снимется первым сохранением. */
  needsReview: boolean;
}

/** Страница объекта в кабинете RealtyCalendar. Формат сверен по живому кабинету 16.09.2026. */
export function rcObjectUrl(rcObjectId: string): string {
  return `https://realtycalendar.ru/apartment/#/${encodeURIComponent(rcObjectId)}/`;
}

export async function fetchProperties(): Promise<PropertyRow[]> {
  return api<PropertyRow[]>('/api/staff/properties');
}

export async function fetchProperty(id: string): Promise<PropertyRow> {
  return api<PropertyRow>(`/api/staff/properties/${id}`);
}

export async function patchProperty(
  id: string,
  patch: { title?: string; city?: string | null; data?: Record<string, unknown> },
): Promise<PropertyRow> {
  return api<PropertyRow>(`/api/staff/properties/${id}`, { method: 'PATCH', json: patch });
}

export async function createProperty(input: {
  title: string;
  city?: string | null;
}): Promise<PropertyRow> {
  return api<PropertyRow>('/api/staff/properties', { method: 'POST', json: input });
}

export interface ParseWarning {
  field?: string;
  message?: string;
}

/**
 * Создаёт НОВЫЙ объект по ссылке на объявление.
 * Именно этот путь, а не /listings/import: тот перезаписывает первый объект аккаунта
 * (наследие демо-стенда) и затёр бы уже заведённую квартиру.
 */
export interface CreateFromUrlResult {
  propertyId: string;
  title: string;
  warnings: ParseWarning[];
  /** true - объявление уже заведено, второй объект не создавали. */
  alreadyExisted: boolean;
  /** true - к существующему объекту прикрепили ещё один адрес того же объявления. */
  sourceAttached: boolean;
}

export async function createPropertyFromUrl(url: string): Promise<CreateFromUrlResult> {
  return api('/api/staff/listings/create-property', { method: 'POST', json: { url } });
}

/** Ссылка на объявление, с которой тянутся данные объекта. */
export interface PropertySource {
  url: string;
  platform: string | null;
  addedAt: string;
  lastParsedAt: string | null;
  lastStatus: 'ok' | 'error' | null;
  lastError: string | null;
}

export interface SourcesResult {
  items: PropertySource[];
  applied: boolean;
  warnings: ParseWarning[];
  /** Вставили ссылку на объект в кабинете RealtyCalendar - сервер связал карточку с лотом. */
  rcObjectId?: string;
}

export interface MergeResult {
  ok: true;
  property: PropertyRow;
  moved: {
    bookings: number;
    listings: number;
    icalFeeds: number;
    kbDocuments: number;
    kbChunks: number;
    kbFacts: number;
    rcMappings: number;
    ingestEvents: number;
    sessions: number;
  };
}

/** `targetId` остаётся, `sourceId` поглощается и удаляется. */
export async function mergeProperty(targetId: string, sourceId: string): Promise<MergeResult> {
  return api(`/api/staff/properties/${targetId}/merge`, { method: 'POST', json: { sourceId } });
}

export async function fetchPropertySources(id: string): Promise<PropertySource[]> {
  const r = await api<{ items: PropertySource[] }>(`/api/staff/properties/${id}/sources`);
  return r.items;
}

/** applyData=false - только привязать ссылку, не перезаписывая анкету текущими данными. */
export async function addPropertySource(
  id: string,
  url: string,
  applyData = true,
): Promise<SourcesResult> {
  return api(`/api/staff/properties/${id}/sources`, { method: 'POST', json: { url, applyData } });
}

export async function refreshPropertySource(id: string, url: string): Promise<SourcesResult> {
  return api(`/api/staff/properties/${id}/sources/refresh`, { method: 'POST', json: { url } });
}

export async function removePropertySource(id: string, url: string): Promise<{ items: PropertySource[] }> {
  return api(`/api/staff/properties/${id}/sources`, { method: 'DELETE', json: { url } });
}

/**
 * Удаление объекта. Сервер отбивает 409, если на квартире есть брони: удалить её значит
 * оставить брони без объекта, а вместе с ними - диалоги гостей без контекста.
 */
export async function deleteProperty(id: string): Promise<void> {
  await api(`/api/staff/properties/${id}`, { method: 'DELETE' });
}
